import { Heart, X, Mail, Share2, Send, Navigation, Star } from "lucide-react";
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useSpMessageLaunch } from '@/features/messaging/hooks/useSpMessageLaunch';
import { isGenericSearch } from '../../constants/client-selection';

type ActionButtonsProps = {
  isExpanded: boolean;
  profileLoading: boolean;
  viewMoreLabel: string;
  onViewProfileClick: () => void;
  showViewProfileTooltip: boolean;
  onViewProfileTooltipEnter: () => void;
  onViewProfileTooltipLeave: () => void;
  formattedDistance: string | null;
  showDistanceTooltip: boolean;
  onDistanceTooltipEnter: () => void;
  onDistanceTooltipLeave: () => void;
  formattedRating: string | null;
  showRatingTooltip: boolean;
  onRatingTooltipEnter: () => void;
  onRatingTooltipLeave: () => void;
  providerId?: string;
  /** Display name shown in the pre-chat intent modal, when it opens (spec §3.3). */
  providerName?: string;
  clientId?: string | null;
  isHearted: boolean;
  isCrossed: boolean;
  onHeartClick: () => void;
  onCrossClick: () => void;
  showMessageTooltip: boolean;
  onMessageTooltipEnter: () => void;
  onMessageTooltipLeave: () => void;
  showHeartTooltip: boolean;
  onHeartTooltipEnter: () => void;
  onHeartTooltipLeave: () => void;
  showShareTooltip: boolean;
  onShareTooltipEnter: () => void;
  onShareTooltipLeave: () => void;
  onShareClick?: () => void;
  /**
   * Feature 071 — send this provider's card to someone with no bQuest account.
   * Sibling to "Share to client", which requires a client already on the
   * platform; this one is for the far more common case where they are not.
   */
  onSendContactCardClick?: () => void;
  showCrossTooltip: boolean;
  onCrossTooltipEnter: () => void;
  onCrossTooltipLeave: () => void;
};

export const ActionButtons = ({
  isExpanded,
  profileLoading,
  viewMoreLabel,
  onViewProfileClick,
  showViewProfileTooltip,
  onViewProfileTooltipEnter,
  onViewProfileTooltipLeave,
  formattedDistance,
  showDistanceTooltip,
  onDistanceTooltipEnter,
  onDistanceTooltipLeave,
  formattedRating,
  showRatingTooltip,
  onRatingTooltipEnter,
  onRatingTooltipLeave,
  providerId,
  providerName,
  clientId,
  isHearted,
  isCrossed,
  onHeartClick,
  onCrossClick,
  showMessageTooltip,
  onMessageTooltipEnter,
  onMessageTooltipLeave,
  showHeartTooltip,
  onHeartTooltipEnter,
  onHeartTooltipLeave,
  showShareTooltip,
  onShareTooltipEnter,
  onShareTooltipLeave,
  onShareClick,
  onSendContactCardClick,
  showCrossTooltip,
  onCrossTooltipEnter,
  onCrossTooltipLeave,
}: ActionButtonsProps) => {
  const { user: loggedInUser } = useAuth();
  // "My Clients" matchmaking with a real selected client is the spec's
  // verified client-linked context (§3.3) — generic search is not.
  const { launch: launchMessage, modal: messageModal } = useSpMessageLaunch({
    serviceProviderId: providerId ?? '',
    providerName: providerName ?? '',
    sourceSurface: 'matchmaking',
    isClientLinked: !isGenericSearch(clientId),
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="relative">
        <button
          type="button"
          onClick={onViewProfileClick}
          disabled={profileLoading}
          onMouseEnter={onViewProfileTooltipEnter}
          onMouseLeave={onViewProfileTooltipLeave}
          className="flex items-center gap-2 text-base font-medium font-termina text-[#026857] disabled:opacity-50 disabled:cursor-not-allowed hover:text-[#025247] transition-colors"
        >
          {profileLoading ? "Loading..." : viewMoreLabel}
          <svg
            className={`h-4 w-4 transition-transform ${isExpanded ? "" : "rotate-180"}`}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M6 15l6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {showViewProfileTooltip && (
          <div className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 pointer-events-none z-50">
            <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina capitalize text-[#111827]">
              {isExpanded ? "Hide full profile details" : "View full profile details"}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        {/* Distance Section - Miles */}
        {formattedDistance && (
          <>
            <div 
              className="relative flex items-center gap-2"
              onMouseEnter={onDistanceTooltipEnter}
              onMouseLeave={onDistanceTooltipLeave}
            >
              <Navigation 
                className="h-6 w-6 shrink-0 text-[#026857]" 
                aria-hidden="true"
              />
              <span className="text-sm font-termina text-[#4B5563]">{formattedDistance} miles</span>
              {showDistanceTooltip && (
                <div className="absolute right-0 bottom-full mb-2 pointer-events-none z-50">
                  <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina capitalize text-[#111827]">
                    Distance from search location
                  </div>
                </div>
              )}
            </div>
            <span className="h-6 w-px bg-[#D7DAE3]" aria-hidden="true" />
          </>
        )}
        
        {/* Rating Section */}
        {formattedRating && (
          <>
            <div
              className="relative flex items-center gap-2 leading-none"
              onMouseEnter={onRatingTooltipEnter}
              onMouseLeave={onRatingTooltipLeave}
            >
              <Star className="h-5 w-5 text-[#026857]" aria-hidden="true" />
              <span className="text-base font-termina text-[#4B5563] leading-none">
                {formattedRating}
              </span>
              {showRatingTooltip && (
                <div className="absolute right-0 bottom-full mb-2 pointer-events-none z-50">
                  <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina capitalize text-[#111827]">
                    Provider rating
                  </div>
                </div>
              )}
            </div>
            <span className="h-6 w-px bg-[#D7DAE3]" aria-hidden="true" />
          </>
        )}
        
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              type="button"
              className="h-9 w-9 rounded-full border border-[#026857] bg-[#026857]/10 flex items-center justify-center hover:bg-[#026857]/20 transition-colors relative"
              aria-label="Send message"
              onClick={() => void launchMessage()}
              onMouseEnter={onMessageTooltipEnter}
              onMouseLeave={onMessageTooltipLeave}
            >
              <Mail className="h-5 w-5 text-[#026857]" />
            </button>
            {showMessageTooltip && (
              <div className="absolute right-0 bottom-full mb-2 pointer-events-none z-50">
                <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina text-[#111827]">
                  Send a Message
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <button 
              type="button" 
              className={isHearted
                ? "h-9 w-9 rounded-full border border-[#026857] bg-[#026857] flex items-center justify-center hover:bg-[#025247] transition-all relative"
                : "h-9 w-9 rounded-full border border-[#026857] bg-[#026857]/10 flex items-center justify-center hover:bg-[#026857]/20 transition-all relative"}
              aria-label={isHearted ? "Remove from network" : "Add to network"}
              onClick={onHeartClick}
              onMouseEnter={onHeartTooltipEnter}
              onMouseLeave={onHeartTooltipLeave}
            >
              <Heart className={`h-4 w-4 ${isHearted ? 'fill-white text-white' : 'text-[#026857]'}`} />
            </button>
            {showHeartTooltip && (
              <div className="absolute right-0 bottom-full mb-2 pointer-events-none z-50">
                <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina capitalize text-[#111827]">
                  {isHearted ? "Remove from favorites" : "Add to favorites"}
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <button 
              type="button" 
              className="h-9 w-9 rounded-full border border-[#026857] bg-[#026857]/10 flex items-center justify-center hover:bg-[#026857]/20 transition-all relative disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Share to client"
              onClick={onShareClick}
              disabled={!clientId || !onShareClick || loggedInUser?.role !== 'FINANCIAL_ADVISOR'}
              onMouseEnter={onShareTooltipEnter}
              onMouseLeave={onShareTooltipLeave}
            >
              <Share2 className="h-5 w-5 text-[#026857]" />
            </button>
            {onSendContactCardClick && loggedInUser?.role === 'FINANCIAL_ADVISOR' && (
              <button
                type="button"
                className="h-9 w-9 rounded-full border border-[#026857] bg-[#026857]/10 flex items-center justify-center hover:bg-[#026857]/20 transition-all ml-2"
                aria-label="Send contact card"
                title="Send contact card"
                onClick={onSendContactCardClick}
              >
                <Send className="h-5 w-5 text-[#026857]" aria-hidden="true" />
              </button>
            )}
            {showShareTooltip && (
              <div className="absolute right-0 bottom-full mb-2 pointer-events-none z-50">
                <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina capitalize text-[#111827]">
                  Share to client
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <button 
              type="button" 
              className={isCrossed
                ? "h-9 w-9 rounded-full border border-[#6B7280] bg-[#6B7280] flex items-center justify-center hover:bg-[#4B5563] transition-all relative"
                : "h-9 w-9 rounded-full border border-[#6B7280] bg-[#6B7280]/10 flex items-center justify-center hover:bg-[#6B7280]/20 transition-all relative"}
              aria-label="Not interested"
              onClick={onCrossClick}
              onMouseEnter={onCrossTooltipEnter}
              onMouseLeave={onCrossTooltipLeave}
            >
              <X className="h-5 w-5" style={{ color: isCrossed ? 'white' : '#6B7280' }} />
            </button>
            {showCrossTooltip && (
              <div className="absolute right-0 bottom-full mb-2 pointer-events-none z-50">
                <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina capitalize text-[#111827]">
                  {isCrossed ? "Show interest" : "Not interested"}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {messageModal}
    </div>
  );
};

